/**
 * Analytics Configuration
 * 
 * Centralized event taxonomy and analytics configuration for GA4 + Meta Pixel.
 * All events are consent-gated and COPPA-compliant.
 * 
 * @see docs/ANALYTICS_PLAYBOOK.md for full documentation
 */

import { MODULES, getModuleFromPath } from "@/lib/modules";

// Analytics configuration with hardcoded publishable IDs
export const ANALYTICS_CONFIG = {
  // GA4 Measurement ID
  ga4MeasurementId: 'G-5FXNJ3NVYW',
  
  // GA4 numeric Property ID (find in GA4 → Admin → Property Settings → "Property ID").
  // Used for deep-linking from /admin/analytics-debug into DebugView and the
  // Events Explorer pre-filtered to source=admin_debug. Leave empty to fall
  // back to the localStorage prompt on first use.
  ga4PropertyId: '',

  // Meta Pixel ID
  metaPixelId: '952878716809971',
  
  // Cross-domain configuration
  crossDomainDomains: [
    'hub.castready.org',
    'castready.org',
    // Add checkout domain if needed: 'checkout.stripe.com'
  ],
  
  // Referral exclusions (don't count these as referrers)
  referralExclusions: [
    'hub.castready.org',
    'castready.org',
    'checkout.stripe.com',
  ],
  
  // Debug mode (logs events to console in development)
  debugMode: import.meta.env.DEV,
} as const;

/**
 * Event Taxonomy
 * 
 * Organized by funnel stage with consistent naming across GA4 + Meta.
 * No PII in event parameters.
 */
export const ANALYTICS_EVENTS = {
  // === ACQUISITION / LANDING ===
  PAGE_VIEW: 'page_view',
  VIEW_LANDING_CALLBACK_ZONE: 'view_landing_callback_zone',
  VIEW_LANDING_COACH: 'view_landing_coach',
  VIEW_LANDING_CASTINGS: 'view_landing_castings',
  VIEW_LANDING_PRO: 'view_landing_pro',
  VIEW_PRICING: 'view_pricing',
  VIEW_COMPARISON_PAGE: 'view_comparison_page',
  VIEW_PERSONA_LANDING: 'view_persona_landing',

  // === MODULE ENGAGEMENT (unified across the 8 modules) ===
  MODULE_VIEW: 'module_view',
  
  // === KIT PURCHASE FUNNEL ===
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_TO_CART: 'add_to_cart',
  SELECT_ITEM: 'select_item',
  VIEW_ITEM: 'view_item',
  ADD_PAYMENT_INFO: 'add_payment_info',
  PURCHASE_KIT: 'purchase_kit',

  // === GA4 STANDARD CONVERSION EVENTS (Meta Ads required) ===
  SIGN_UP: 'sign_up',
  GENERATE_LEAD: 'generate_lead',
  
  // === BOX → DIGITAL ATTACH ===
  QR_SCAN: 'qr_scan',
  REDEMPTION_LANDING_VIEW: 'redemption_landing_view',
  REDEMPTION_START: 'redemption_start',
  REDEMPTION_COMPLETE: 'redemption_complete',
  
  // === SIGNUP / ACCOUNT ===
  SIGN_UP_START: 'sign_up_start',
  SIGN_UP_COMPLETE: 'sign_up_complete',
  LOGIN: 'login',
  
  // === SUBSCRIPTION / ATTACH ===
  VIEW_SUBSCRIPTION_OFFER: 'view_subscription_offer',
  START_TRIAL: 'start_trial',
  SUBSCRIBE_PREMIUM: 'subscribe_premium',
  
  // === ACTIVATION (FIRST WIN) ===
  START_PRACTICE_PLAN: 'start_practice_plan',
  COMPLETE_PRACTICE_PLAN: 'complete_practice_plan',
  EARN_BADGE: 'earn_badge',
  PARENT_DASHBOARD_VIEW: 'parent_dashboard_view',
  FIRST_AI_FEATURE_USE: 'first_ai_feature_use',
  
  // === PARTNER ATTRIBUTION ===
  PARTNER_REFERRAL_CLICK: 'partner_referral_click',
  PARTNER_LEAD_SUBMIT: 'partner_lead_submit',
  BOOK_DEMO: 'book_demo',
  
  // === B2B FUNNEL ===
  REQUEST_DEMO_SUBMIT: 'request_demo_submit',
  CONTACT_SALES_SUBMIT: 'contact_sales_submit',
  PARTNER_APPLY_SUBMIT: 'partner_apply_submit',

  // === WEDGE FUNNEL (90-day plan) ===
  HOMEPAGE_CTA_CLICK: 'homepage_cta_click',
  PRICING_CTA_CLICK: 'pricing_cta_click',
  HEADER_CTA_CLICK: 'header_cta_click',
  BOOK_DEMO_VIEW: 'book_demo_view',
  BOOK_DEMO_SCHEDULED: 'book_demo_scheduled',
  
  // === CONTENT ENGAGEMENT ===
  VIEW_BLOG_POST: 'view_blog_post',
  SHARE_BLOG_POST: 'share_blog_post',
  
  // === HMU PLATFORM ===
  VIEW_LANDING_HMU: 'view_landing_hmu',
  HMU_BOOKING_REQUEST: 'hmu_booking_request',
  HMU_BOOKING_CONFIRM: 'hmu_booking_confirm',
  HMU_PORTFOLIO_UPLOAD: 'hmu_portfolio_upload',
  
  // === CREW PLATFORM ===
  VIEW_LANDING_CREW: 'view_landing_crew',
  CREW_APPLICATION_SUBMIT: 'crew_application_submit',
  CREW_PROJECT_CREATE: 'crew_project_create',
  CREW_REVIEW_SUBMIT: 'crew_review_submit',
  
  // === DRAFTLINE PLATFORM ===
  VIEW_LANDING_DRAFTLINE: 'view_landing_draftline',
  
  // === CAPTURE PLATFORM ===
  VIEW_LANDING_CAPTURE: 'view_landing_capture',

  // === SYSTEM / NAVIGATION ===
  PAGE_NOT_FOUND: 'page_not_found',
  ENTERPRISE_QUOTE_VIEW: 'enterprise_quote_view',

  // === FOCUS LOOP (pilot proof: Coach -> Actor -> AI -> Parent) ===
  // assignment_created     params: { coach_id, studio_id, actor_id, assignment_id, assignment_type, due_date }
  // submission_created     params: { submission_id, actor_id, assignment_id, created_at }
  // assignment_completed   params: { actor_id, assignment_id, submission_id, days_to_complete }
  // ai_feedback_generated  params: { submission_id, generated_at, time_to_feedback_seconds, feedback_type }
  // parent_report_viewed   params: { parent_id, actor_id, report_type }
  ASSIGNMENT_CREATED: 'assignment_created',
  ASSIGNMENT_COMPLETED: 'assignment_completed',
  SUBMISSION_CREATED: 'submission_created',
  AI_FEEDBACK_GENERATED: 'ai_feedback_generated',
  PARENT_REPORT_VIEWED: 'parent_report_viewed',

  // === GTM CTAs / LEGACY ONBOARDING RETIREMENT ===
  GTM_SELF_SERVE_CLICKED: 'gtm_self_serve_clicked',
  GTM_BOOK_CALL_CLICKED: 'gtm_book_call_clicked',
  LEGACY_ONBOARDING_REDIRECTED: 'legacy_onboarding_redirected',

  // === UPSELL FUNNEL ===
  UPSELL_MODAL_SHOWN: 'upsell_modal_shown',
  UPSELL_MODAL_CTA: 'upsell_modal_cta',

  // === INVITE ACCEPTANCE (Pro / Admin) ===
  PRO_INVITE_VIEW: 'pro_invite_view',
  PRO_INVITE_ACCEPT: 'pro_invite_accept',
  PRO_INVITE_COMPLETE: 'pro_invite_complete',
  ADMIN_INVITE_VIEW: 'admin_invite_view',
  ADMIN_INVITE_ACCEPT: 'admin_invite_accept',
  ADMIN_INVITE_COMPLETE: 'admin_invite_complete',
} as const;

/**
 * GA4 Standard Event Mirror
 *
 * Maps internal custom event names to GA4 standard conversion event names.
 * When a source event fires via trackBoth(), the mirrored standard event
 * is also emitted to GA4 (only — Meta has its own standard mapping in
 * META_EVENT_MAPPING). This guarantees the events Meta Ads / GA4
 * conversion reporting expect (`sign_up`, `generate_lead`, `purchase_kit`)
 * exist without renaming the rich custom events the rest of the app uses.
 */
export const GA4_STANDARD_MIRROR: Record<string, { name: string; extra?: Record<string, unknown> }> = {
  sign_up_complete: { name: 'sign_up' },
  pro_invite_complete: { name: 'sign_up', extra: { method: 'pro_invite' } },
  admin_invite_complete: { name: 'sign_up', extra: { method: 'admin_invite' } },
  request_demo_submit: { name: 'generate_lead', extra: { lead_type: 'demo' } },
  contact_sales_submit: { name: 'generate_lead', extra: { lead_type: 'enterprise' } },
  partner_apply_submit: { name: 'generate_lead', extra: { lead_type: 'partner' } },
  partner_lead_submit: { name: 'generate_lead', extra: { lead_type: 'partner_referral' } },
  book_demo_scheduled: { name: 'generate_lead', extra: { lead_type: 'demo_scheduled' } },
  hmu_booking_request: { name: 'generate_lead', extra: { lead_type: 'hmu_booking' } },
  crew_application_submit: { name: 'generate_lead', extra: { lead_type: 'crew' } },
};

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

/**
 * Funnel-stage required parameter contract.
 *
 * Each entry lists the parameter keys that MUST appear in the literal object
 * passed to `trackBoth('<event>', { ... })` / `trackOnce(...)`. Optional
 * params (e.g. `utm_*`, coupon) are intentionally excluded — they're
 * captured globally via `campaignAttribution` and don't need to be inlined
 * at every call site.
 *
 * Validated by `scripts/audit-analytics-events.ts` and surfaced in
 * `docs/runbooks/GA4_CONVERSION_VERIFICATION.md` as the locked funnel
 * contract before any CRO change.
 */
export const FUNNEL_REQUIRED_PARAMS: Record<string, readonly string[]> = {
  // Stage 1 — landings
  view_pricing: ['tier_shown'],

  // Stage 2 — primary CTAs
  homepage_cta_click: ['cta_id', 'cta_location', 'destination'],
  pricing_cta_click: ['cta_id', 'cta_location', 'destination'],
  header_cta_click: ['cta_id', 'destination', 'surface'],

  // Stage 3-4 — signup
  sign_up_start: ['method', 'account_type'],
  sign_up_complete: ['method', 'account_type'],

  // Stage 5-7 — purchase funnel
  begin_checkout: ['currency', 'value', 'items', 'checkout_type'],
  add_payment_info: ['payment_type', 'value', 'currency'],
  purchase_kit: ['transaction_id', 'value', 'currency', 'items'],

  // Lead path
  request_demo_submit: ['form_id', 'lead_type'],
  contact_sales_submit: ['form_id', 'lead_type'],
  book_demo_scheduled: ['slot_at', 'calendar_provider'],

  // Box-attach
  qr_scan: ['box_code', 'source'],
  redemption_complete: ['box_code', 'attached_to_account_type'],

  // Relaunch funnel
  marketing_to_hub_referral: ['utm_source', 'utm_campaign', 'role_hint'],
  signup_role_selected: ['role', 'source', 'has_utms'],
  trial_started: ['platform', 'tier', 'trial_days'],
  pilot_apply_submitted: ['org_type', 'seats_estimated'],
  upsell_modal_shown: ['reason', 'platform'],
  upsell_modal_cta: ['reason', 'action'],

  // Invite funnels
  pro_invite_view: ['invite_type', 'partner_type'],
  pro_invite_accept: ['invite_type', 'is_existing_user'],
  pro_invite_complete: ['invite_type', 'partner_type'],
  admin_invite_view: ['invite_type', 'team_role'],
  admin_invite_accept: ['invite_type', 'is_existing_user'],
  admin_invite_complete: ['invite_type', 'team_role'],
} as const;

/**
 * Meta Pixel Standard Event Mapping
 * 
 * Maps internal event names to Meta standard events for better attribution.
 */
export const META_EVENT_MAPPING: Partial<Record<AnalyticsEventName, string>> = {
  [ANALYTICS_EVENTS.PAGE_VIEW]: 'PageView',
  [ANALYTICS_EVENTS.MODULE_VIEW]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_PRICING]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_CALLBACK_ZONE]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_COACH]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_CASTINGS]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_PRO]: 'ViewContent',
  [ANALYTICS_EVENTS.SIGN_UP_START]: 'Lead',
  [ANALYTICS_EVENTS.SIGN_UP_COMPLETE]: 'CompleteRegistration',
  [ANALYTICS_EVENTS.BEGIN_CHECKOUT]: 'InitiateCheckout',
  [ANALYTICS_EVENTS.ADD_TO_CART]: 'AddToCart',
  [ANALYTICS_EVENTS.VIEW_ITEM]: 'ViewContent',
  [ANALYTICS_EVENTS.ADD_PAYMENT_INFO]: 'AddPaymentInfo',
  [ANALYTICS_EVENTS.PURCHASE_KIT]: 'Purchase',
  [ANALYTICS_EVENTS.SUBSCRIBE_PREMIUM]: 'Subscribe',
  [ANALYTICS_EVENTS.START_TRIAL]: 'StartTrial',
  [ANALYTICS_EVENTS.BOOK_DEMO]: 'Schedule',
  [ANALYTICS_EVENTS.REQUEST_DEMO_SUBMIT]: 'Lead',
  [ANALYTICS_EVENTS.CONTACT_SALES_SUBMIT]: 'Lead',
  [ANALYTICS_EVENTS.PARTNER_APPLY_SUBMIT]: 'Lead',
  [ANALYTICS_EVENTS.PARTNER_LEAD_SUBMIT]: 'Lead',
  [ANALYTICS_EVENTS.REDEMPTION_START]: 'Lead',
  [ANALYTICS_EVENTS.REDEMPTION_COMPLETE]: 'CompleteRegistration',
  [ANALYTICS_EVENTS.REDEMPTION_LANDING_VIEW]: 'ViewContent',
  [ANALYTICS_EVENTS.BOOK_DEMO_VIEW]: 'ViewContent',
  [ANALYTICS_EVENTS.BOOK_DEMO_SCHEDULED]: 'Schedule',
  [ANALYTICS_EVENTS.HOMEPAGE_CTA_CLICK]: 'Lead',
  [ANALYTICS_EVENTS.PRICING_CTA_CLICK]: 'Lead',
  [ANALYTICS_EVENTS.VIEW_LANDING_HMU]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_CREW]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_DRAFTLINE]: 'ViewContent',
  [ANALYTICS_EVENTS.VIEW_LANDING_CAPTURE]: 'ViewContent',
  [ANALYTICS_EVENTS.HMU_BOOKING_REQUEST]: 'Lead',
  [ANALYTICS_EVENTS.CREW_APPLICATION_SUBMIT]: 'Lead',
  [ANALYTICS_EVENTS.CREW_PROJECT_CREATE]: 'Lead',
};

/**
 * Content Groups for GA4 Reporting
 * 
 * Used to categorize pages for aggregate reporting.
 */
export const CONTENT_GROUPS = {
  MARKETING: 'marketing',
  HUB_APP: 'hub_app',
  PLATFORM_CALLBACK_ZONE: 'platform_callback_zone',
  PLATFORM_COACH: 'platform_coach',
  PLATFORM_CASTINGS: 'platform_castings',
  PLATFORM_PRO: 'platform_pro',
  PLATFORM_HMU: 'platform_hmu',
  PLATFORM_CREW: 'platform_crew',
  PLATFORM_DRAFTLINE: 'platform_draftline',
  PLATFORM_CAPTURE: 'platform_capture',
  RESOURCES: 'resources',
  AUTH: 'auth',
  ADMIN: 'admin',
} as const;

/**
 * Determine content group from URL path
 */
export function getContentGroup(pathname: string): string {
  // Derive module content groups from the single MODULES registry.
  const mod = getModuleFromPath(pathname);
  if (mod) return mod.contentGroup;
  // Callback Zone aliases — actor-facing routes that pre-date the registry.
  if (pathname === '/actor' || pathname === '/lessons' || pathname === '/community') {
    return CONTENT_GROUPS.PLATFORM_CALLBACK_ZONE;
  }
  if (pathname.startsWith('/admin')) {
    return CONTENT_GROUPS.ADMIN;
  }
  if (pathname === '/auth' || pathname === '/onboarding') {
    return CONTENT_GROUPS.AUTH;
  }
  if (pathname.startsWith('/resources') || pathname.startsWith('/blog') || pathname === '/faq') {
    return CONTENT_GROUPS.RESOURCES;
  }
  // Marketing pages (landing, pricing, comparison, etc.)
  return CONTENT_GROUPS.MARKETING;
}

/**
 * Sanitize event parameters to remove any potential PII
 * 
 * CRITICAL: Never send email, name, phone, exact DOB, school names, or addresses
 */
export function sanitizeEventParams(params: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  
  const PII_KEYS = [
    'email', 'name', 'first_name', 'last_name', 'phone', 'telephone',
    'address', 'street', 'city', 'zip', 'postal', 'school', 'birthdate',
    'date_of_birth', 'dob', 'ssn', 'social_security'
  ];
  
  for (const [key, value] of Object.entries(params)) {
    const lowerKey = key.toLowerCase();
    
    // Skip PII keys
    if (PII_KEYS.some(pii => lowerKey.includes(pii))) {
      continue;
    }
    
    // Skip email-like values
    if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
      continue;
    }
    
    sanitized[key] = value;
  }
  
  return sanitized;
}

export default ANALYTICS_CONFIG;
